import { usePinnedEvents } from "@/hooks/usePinnedEvents";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Pin, ExternalLink } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

export const PinnedEvents = () => {
  const { data: events, isLoading } = usePinnedEvents();

  if (isLoading) {
    return (
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-80" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!events || events.length === 0) {
    return null;
  }

  const getCountdown = (eventDate: string) => {
    const days = differenceInDays(new Date(eventDate), new Date());
    if (days === 0) return "Today!";
    if (days === 1) return "Tomorrow";
    return `${days} days away`;
  };

  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pin className="h-6 w-6 text-primary" />
              <h2 className="text-3xl font-bold">Upcoming Events</h2>
            </div>
            <p className="text-muted-foreground">
              Events happening soon - don't miss out
            </p>
          </div>
          <Link to="/events">
            <Button variant="ghost">
              See All
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card key={event.id} className="hover:shadow-lg transition-shadow">
              {event.image_url && (
                <div className="relative overflow-hidden rounded-t-lg">
                  <img
                    src={event.image_url}
                    alt={event.event_name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-primary/90 backdrop-blur-sm">
                      <Pin className="h-3 w-3 mr-1" />
                      Pinned
                    </Badge>
                  </div>
                </div>
              )}
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="secondary" className="text-xs">
                    {getCountdown(event.event_date)}
                  </Badge>
                  {event.is_featured && (
                    <Badge variant="outline" className="text-xs">
                      Featured
                    </Badge>
                  )}
                </div>
                <CardTitle className="line-clamp-2">{event.event_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    <span>{format(new Date(event.event_date), "EEEE, MMM dd, yyyy")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span className="line-clamp-1">{event.location}</span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {event.description}
                </p>

                {event.has_dedicated_page && event.slug ? (
                  <Button asChild className="w-full">
                    <Link to={`/events/${event.slug}`}>
                      View Details
                    </Link>
                  </Button>
                ) : (
                  <Button asChild variant="outline" className="w-full">
                    <a
                      href={event.event_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Register Now
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
